import { alpha, NeutralColors } from "@mui/material/styles";

import { ACCENT, BRAND, DATA, NEUTRAL, SIGNAL } from "./tokens";

import type { PaletteColor } from "@mui/material";

/**
 * The MUI palette, expressed in the MACONIA 2026 tokens.
 *
 * Values come from ./tokens.ts rather than being typed out again — MUI is the second
 * place a colour can go stale (Tailwind is the first), and a palette that is right in
 * one and wrong in the other shows up in exactly one component and nowhere else.
 *
 * `main` is what MUI puts under text and small fills, so every `main` here clears
 * 4.5:1 on white. `#FF5A24` is a fill, never an ink — it appears as `light` on the
 * primary colour and on nothing else. See docs/brand-implementation-guide.md §6.
 */

const withAlphas = (color: PaletteColor): PaletteColor => {
	return {
		...color,
		alpha4: alpha(color.main, 0.04),
		alpha8: alpha(color.main, 0.08),
		alpha12: alpha(color.main, 0.12),
		alpha30: alpha(color.main, 0.3),
		alpha50: alpha(color.main, 0.5),
	};
};

/**
 * The brand preset. `main` is `accent-strong` — the only orange that may be written
 * with — so MUI's default "primary means text too" behaviour stays legible. The raw
 * accent lives on `light`, for fills that opt into it deliberately.
 */
export const maconia: PaletteColor = {
	light: ACCENT.accent,
	main: ACCENT.accentStrong,
	dark: ACCENT.accentDark,
	contrastText: BRAND.white,
};

/** Kept under its old name so existing `colorPreset="browserbite"` call sites resolve. */
export const browserbite: PaletteColor = maconia;

/** The warm ramp. Numeric names are MUI's; the values are the 2026 neutrals. */
export const neutral: NeutralColors = {
	50: NEUTRAL[25],
	100: NEUTRAL[50],
	200: NEUTRAL[200],
	300: NEUTRAL[300],
	400: NEUTRAL[400],
	500: NEUTRAL[500],
	600: NEUTRAL[600],
	700: NEUTRAL[700],
	800: "#2A2925",
	900: NEUTRAL[900],
};

export const yellow = withAlphas({
	lightest: "#FFFBEB",
	light: SIGNAL.warningLight,
	main: SIGNAL.warningInk, // #D9A400 is 2.27:1 — amber cannot be written on itself.
	dark: "#5E3F00",
	darkest: "#3D2900",
	contrastText: BRAND.white,
});

export const blue = withAlphas({
	lightest: "#FAF9F8",
	light: ACCENT.accentTint,
	main: ACCENT.accentStrong,
	dark: ACCENT.accentDark,
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const green = withAlphas({
	lightest: "#F6FEF9",
	light: SIGNAL.successLight,
	main: DATA.severityLow, // #198754 — 4.53:1, unlike the brighter `success` fill.
	dark: "#146C43",
	darkest: "#0B4429",
	contrastText: BRAND.white,
});

export const indigo = withAlphas({
	lightest: "#F7F5FB",
	light: "#E7E1F3",
	main: DATA.plum,
	dark: "#523C7C",
	darkest: "#332654",
	contrastText: BRAND.white,
});

export const purple = withAlphas({
	lightest: "#F7F5FB",
	light: "#E7E1F3",
	main: DATA.plum,
	dark: "#523C7C",
	darkest: "#332654",
	contrastText: BRAND.white,
});

export const success = withAlphas({
	lightest: "#F6FEF9",
	light: SIGNAL.successLight,
	main: DATA.severityLow, // The readable green; SIGNAL.success is a large/bold fill.
	dark: "#146C43",
	darkest: "#0B4429",
	contrastText: BRAND.white,
});

export const info = withAlphas({
	lightest: NEUTRAL[25],
	light: NEUTRAL[100],
	main: BRAND.subtle,
	dark: NEUTRAL[700],
	darkest: BRAND.ink,
	contrastText: BRAND.white,
});

export const warning = withAlphas({
	lightest: "#FFFBEB",
	light: SIGNAL.warningLight,
	main: SIGNAL.warningInk,
	dark: "#5E3F00",
	darkest: "#3D2900",
	contrastText: BRAND.white,
});

export const error = withAlphas({
	lightest: "#FFF5F4",
	light: SIGNAL.errorLight,
	main: SIGNAL.danger,
	dark: "#B01F00",
	darkest: "#6E1400",
	contrastText: BRAND.white,
});
